import i18next from 'i18next';
import type {
    UI5Version,
    ExportParametersV2,
    ExportAnalyticalListPageV2Parameters,
    ExportListReportV2Parameters,
    ExportObjectPageV2Parameters,
    ExportOverviewPageV2Parameters,
    ExportListReportWithPathV2Parameters,
    ExportV2WithPath
} from '../../..';
import type {
    ExportResults,
    FileData,
    FioriElementsVersion,
    Manifest,
    v2,
    ExportPageUnitionType,
    ExportOVPWithPathParameters
} from '@sap/ux-specification-types';
import {
    ChangeIndicator,
    LogSeverity,
    MANIFESTPATH,
    ManifestSection,
    PageTypeV2,
    SchemaType,
    SAPUI5_FRAGMENT_CLASS
} from '@sap/ux-specification-types';
import type { ObjectPage } from './pages';
import { deleteEmptyStructure, getPageLayoutInformation, getReflectMetadata } from '../../common';
import { ApplicationV2, AppSettings } from '../application';
import { ensureManifestSectionByPathV2, getManifestSectionByPathV2, transformPageToNewManifestV2 } from './manifest';
import { findListReportPageV2, findAnalyticalListPage, findObjectPageV2 } from '../import';
import { MetadataInstanceFactoryV2 as Factory } from './factory';
import { transferManifestEntriesAndFlexChange, transferManifestEntriesOVP } from './export';
import type { MetadataInstanceInterface } from '../../common/decoration/factory';
import type { TransferParameterTypeV2 } from '../types';
import { log } from '../../../extensionLogger';
import type { Definition } from 'typescript-json-schema';
import { createIdGenerator, type IdGeneratorFunction } from '@sap-ux/fe-fpm-writer';

// Temporary solution:
// pageAccess.ts will reroute its function exportListReportPage to the function with the same name in this file.
// Hence, exportListReportPageV2 is actually the function exportListReportPage from this file.
// For manual tests this can be changed by switching useGenericSchemaHandling in pageAccess.ts to true.
// In this case generic schema handling an the schema for ListReportNew will be used.
import { exportListReportPage as exportListReportPageV2 } from '../genericSchemaHandling/pages/pageAccess';

/**
 * Creates and returns a new export results object initialized with default values.
 *
 * @param fragmentFiles - Optional array of file data to include in the export results.
 * @returns An object containing default export results, including flex changes, manifest, and fragments.
 */
const getDefaultExportResult = (fragmentFiles?: FileData[]): ExportResults => {
    return {
        flexChanges: [],
        manifest: {
            [ManifestSection.generic]: { pages: {} }
        } as unknown as Manifest,
        manifestChangeIndicator: ChangeIndicator.NoChange,
        fragments: fragmentFiles
            ? fragmentFiles.map((frg) => {
                  return {
                      dataSourceUri: frg.dataSourceUri,
                      fileContent: frg.fileContent,
                      changeIndicator: ChangeIndicator.NoChange
                  };
              })
            : []
    };
};

/**
 * Generates a unique ID generator function based on the content of provided fragment and view files.
 * This function creates a cache of file contents from the given fragment and view files, and then uses this cache to create an ID generator function.
 * The generated ID generator function can be used to create unique IDs while ensuring that they do not conflict with existing IDs in the provided files.
 *
 * @param fragmentFiles - Optional array of file data representing fragments, used to build the content cache for ID generation.
 * @param views - Optional array of file data representing views, also used to build the content cache for ID generation.
 * @returns A function that generates unique IDs, ensuring they do not conflict with existing IDs in the provided fragment and view files.
 */
function getIdGeneratorFunction(fragmentFiles: FileData[] = [], views: FileData[] = []): IdGeneratorFunction {
    const fileContentCache = [...fragmentFiles, ...views].reduce((acc: string[], file: FileData) => {
        if (file.fileContent) {
            acc.push(file.fileContent);
        }
        return acc;
    }, []);
    return createIdGenerator({
        fileContentCache
    }) as IdGeneratorFunction;
}

/**
 * Instantiates the export classes for a single object page section.
 *
 * @param currentSection - Export class as an object, to be updated
 * @param sectionId - Current section ID
 * @param {MetadataInstanceInterface} factory - factory for creating metadata instances
 */
export function getClassesForSingleSection(
    currentSection: object,
    sectionId: string,
    factory: MetadataInstanceInterface
): void {
    if (currentSection && sectionId === 'custom') {
        // Custom sections
        for (const index in currentSection) {
            if (currentSection[index].className === SAPUI5_FRAGMENT_CLASS) {
                currentSection[index] = factory.createInstance(
                    PageTypeV2.ObjectPage,
                    'ObjectPageCustomSectionFragment',
                    currentSection[index]
                );
            } else {
                currentSection[index] = factory.createInstance(
                    PageTypeV2.ObjectPage,
                    'ObjectPageCustomSectionView',
                    currentSection[index]
                );
            }
        }
    }
}

/**
 * Recursive sub-function for handling subsections.
 *
 * @param currentSection - Object page section in config
 * @param factory - A factory instance for creating metadata instances
 */
export function getSubsectionClasses(currentSection: object, factory: MetadataInstanceInterface): void {
    if (currentSection?.['subsections']) {
        let subSection: object;

        for (const subSectionId in currentSection['subsections']) {
            subSection = currentSection['subsections'][subSectionId];
            getClassesForSingleSection(subSection, subSectionId, factory);
            getSubsectionClasses(subSection, factory);
        }
    }
}

/**
 * Instantiates the export classes for the sections of an object page.
 *
 * @param objectPageConfig - The configuration object for the object page.
 * @param objectPage - The export class instance for the object page where sections will be included.
 * @param factory - A factory instance for creating metadata instances.
 */
export function getExportClassesForSections(
    objectPageConfig: v2.ObjectPageConfigV2,
    objectPage: ObjectPage,
    factory: MetadataInstanceInterface
): void {
    // Main function
    let section: object;

    for (const sectionId in objectPageConfig.sections) {
        section = objectPage.sections[sectionId];
        getClassesForSingleSection(section, sectionId, factory);
        getSubsectionClasses(section, factory);
    }
}

/**
 * Determines and validates a v2 listreport page from manifest information.
 *
 * @param {string[]} pageKeys - list of page keys, to be filled by the find logic
 * @param {ExportListReportV2Parameters} exportParameters - import parameters for the export
 * @returns the v2 list report page
 */
function findV2Page(pageKeys: string[], exportParameters: ExportListReportV2Parameters): v2.SapUiAppPageV2 | undefined {
    const v2Page = findListReportPageV2(
        exportParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
        pageKeys
    );
    if (!v2Page) {
        log(exportParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOLR'),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.generic]
            }
        });
        return;
    }
    if (!v2Page.component) {
        log(exportParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('COMPONENTNOTDEFINED', { pageName: PageTypeV2.ListReport }),
            location: {
                path: MANIFESTPATH
            }
        });
        return;
    }
    if (!v2Page.component.settings) {
        v2Page.component.settings = {};
    }
    return v2Page;
}

/**
 * Calculates the baseId of the app for flex changes.
 *
 * @param {object} config - content of the config file
 * @param {ExportPageUnitionType} exportParameters - all API parameters needed for the export
 * @returns the baseId for changes
 */
export function calculateBaseId(config: object, exportParameters: ExportPageUnitionType): string {
    const pageLayoutInformation = getPageLayoutInformation(config);
    return `${exportParameters.appId}::${pageLayoutInformation.id}::${exportParameters.page.entitySet}--`;
}

/**
 * Run through the given ListReport config and return respective manifest entry and flex changes.
 *
 * @param {ExportListReportV2Parameters} exportParameters - all API parameters needed for the export
 * @param ui5Version - SAP UI5 version
 * @param deletionRequest - if set to true, any manifest setting specified by entityPath gets deleted even if it comprises any unknown property
 * @returns ExportResults - The export result comprises the enhanced manifest as well as a list of flex changes.
 */
export function exportListReportPage(
    exportParameters: ExportListReportV2Parameters | ExportListReportWithPathV2Parameters,
    ui5Version: UI5Version,
    deletionRequest = false
): ExportResults {
    const exportResults: ExportResults = getDefaultExportResult(exportParameters.fragments);
    exportResults.manifest = JSON.parse(JSON.stringify(exportParameters.manifest));
    const idGeneratorFunction = getIdGeneratorFunction(exportParameters?.fragments, exportParameters?.views);

    const listReportConfig = exportParameters.page.config as v2.ListReportConfigV2;
    if (listReportConfig && Object.keys(listReportConfig).length !== 0) {
        const pageKeys: string[] = [];
        const v2Page = findV2Page(pageKeys, exportParameters);
        const factory = new Factory();
        const pageInstance = factory.createPageInstance(PageTypeV2.ListReport, listReportConfig);

        const transferParameters: TransferParameterTypeV2 = {
            v2Page,
            exportResults,
            appId: exportParameters.appId,
            baseId: calculateBaseId(pageInstance, exportParameters),
            appSchema: exportParameters.jsonSchema,
            ui5Version,
            factory,
            pageKeys,
            pageType: PageTypeV2.ListReport,
            logger: exportParameters.logger,
            entityPathArray: exportParameters['entityPath'],
            deletionRequest,
            idGeneratorFunction
        };

        transferManifestEntriesAndFlexChange(
            transferParameters,
            pageInstance,
            [],
            [],
            exportParameters.jsonSchema //starting point for target definition
        );
    }

    return exportResults;
}

/**
 * Run through the given AnalyticalListPage config and return respective manifest entry and flex changes.
 *
 * @param {ExportAnalyticalListPageV2Parameters} exportParameters - all API parameters needed for the export
 * @param ui5Version - SAP UI5 version
 * @param deletionRequest - if set to true, any manifest setting specified by entityPath gets deleted even if it comprises any unknown property
 * @returns ExportResults - The export result comprises the enhanced manifest as well as a list of flex changes.
 */
function exportAnalyticalListPage(
    exportParameters: ExportAnalyticalListPageV2Parameters,
    ui5Version: UI5Version,
    deletionRequest = false
): ExportResults {
    const analyticalListPageConfig = exportParameters.page.config;
    const idGeneratorFunction = getIdGeneratorFunction(exportParameters.fragments, exportParameters.views);
    const exportResults: ExportResults = getDefaultExportResult(exportParameters.fragments);
    exportResults.flexChanges = [];
    exportResults.manifest = JSON.parse(JSON.stringify(exportParameters.manifest));

    //Instantiate base ID
    const pageKeys: string[] = [];

    const v2Page = findAnalyticalListPage(
        exportParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
        pageKeys,
        exportParameters.logger
    );
    if (!v2Page) {
        return exportResults;
    }
    if (!v2Page.component.settings) {
        v2Page.component.settings = {};
    }
    exportResults.manifest = JSON.parse(JSON.stringify(exportParameters.manifest));

    if (analyticalListPageConfig && Object.keys(analyticalListPageConfig).length !== 0) {
        const factory = new Factory();
        const pageInstance = factory.createPageInstance(PageTypeV2.AnalyticalListPage, analyticalListPageConfig);

        const transferParameters: TransferParameterTypeV2 = {
            v2Page,
            exportResults,
            appId: exportParameters.appId,
            baseId: calculateBaseId(pageInstance, exportParameters),
            appSchema: exportParameters.jsonSchema,
            ui5Version,
            factory,
            pageKeys,
            pageType: PageTypeV2.AnalyticalListPage,
            logger: exportParameters.logger,
            entityPathArray: exportParameters['entityPath'],
            deletionRequest,
            idGeneratorFunction
        };

        transferManifestEntriesAndFlexChange(
            transferParameters,
            pageInstance,
            [],
            [],
            exportParameters.jsonSchema //starting point for target definition
        );
    }

    return exportResults;
}

/**
 * Run through the given ObjectPage config and return respective manifest entry and flex changes.
 *
 * @param {ExportObjectPageV2Parameters} exportParameters - all API parameters needed for the export
 * @param ui5Version - SAP UI5 version
 * @param deletionRequest - if set to true, any manifest setting specified by entityPath gets deleted even if it comprises any unknown property
 * @returns ExportResults - The export result comprises the enhanced manifest as well as a list of flex changes.
 */
function exportObjectPage(
    exportParameters: ExportObjectPageV2Parameters,
    ui5Version: UI5Version,
    deletionRequest = false
): ExportResults {
    const objectPageConfig = exportParameters.page.config as v2.ObjectPageConfigV2;
    const entitySet = exportParameters.page.entitySet;
    const idGeneratorFunction = getIdGeneratorFunction(exportParameters.fragments, exportParameters.views);
    const exportResults: ExportResults = getDefaultExportResult(exportParameters.fragments);
    exportResults.manifest = JSON.parse(JSON.stringify(exportParameters.manifest));

    if (objectPageConfig) {
        const pageKeys: string[] = [];
        const v2Page: v2.SapUiAppPageV2 = findObjectPageV2(
            exportParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
            entitySet,
            pageKeys
        );
        if (!v2Page) {
            log(exportParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOOBJECTPAGE', { entitySetName: entitySet }),
                location: {
                    path: MANIFESTPATH,
                    range: [ManifestSection.generic]
                }
            });
            return exportResults;
        }
        const factory = new Factory();
        const pageInstance = factory.createPageInstance(PageTypeV2.ObjectPage, objectPageConfig);
        if (objectPageConfig.sections) {
            getExportClassesForSections(objectPageConfig, pageInstance, factory);
        }
        if (Object.keys(objectPageConfig).length !== 0) {
            const transferParameters: TransferParameterTypeV2 = {
                v2Page,
                exportResults,
                appId: exportParameters.appId,
                baseId: calculateBaseId(pageInstance, exportParameters),
                appSchema: exportParameters.jsonSchema,
                ui5Version,
                factory,
                pageKeys,
                pageType: PageTypeV2.ObjectPage,
                logger: exportParameters.logger,
                entityPathArray: exportParameters['entityPath'],
                deletionRequest,
                idGeneratorFunction
            };

            transferManifestEntriesAndFlexChange(
                transferParameters,
                pageInstance,
                [],
                [],
                exportParameters.jsonSchema //starting point for target definition
            );
        }
    }
    return exportResults;
}
/**
 * Run through the given ObjectPage config and return respective manifest entry and flex changes.
 *
 * @param {ExportOverviewPageV2Parameters} exportParameters - all API parameters needed for the export
 * @returns ExportResults - The export result comprises the enhanced manifest as well as a list of flex changes.
 */
const exportOverviewPage = (
    exportParameters: ExportOverviewPageV2Parameters | ExportOVPWithPathParameters
): ExportResults => {
    const exportResults: ExportResults = getDefaultExportResult();
    exportResults.manifest = JSON.parse(JSON.stringify(exportParameters.manifest));
    const factory = new Factory();
    const overviewPage = factory.createPageInstance(PageTypeV2.OverviewPage, exportParameters.page.config);

    if (exportParameters.page.config && Object.keys(exportParameters.page.config).length !== 0) {
        transferManifestEntriesOVP(
            overviewPage,
            exportResults,
            exportParameters.jsonSchema,
            exportParameters.jsonSchema,
            factory,
            [],
            exportParameters['entityPath']
        );
    }

    return exportResults;
};

/**
 * Exports a Fiori Element V2 application.
 * It converts the entries of the input application (config file) to corresponding manifest settings and flex changes.
 *
 * @param application - content of the config file of an application
 * @param manifest - app descriptor (manifest.json)
 * @param jsonSchema - application specific schema
 * @returns ExportResults - The export result comprises the enhanced manifest as well as a list of flex changes.
 */
const exportApplicationV2 = (application: ApplicationV2, manifest: object, jsonSchema: Definition): ExportResults => {
    const exportResults: ExportResults = getDefaultExportResult();
    exportResults.manifest = JSON.parse(JSON.stringify(manifest));

    //transfer application settings
    const applicationV2 = Object.assign(new ApplicationV2(), application);
    applicationV2.settings = Object.assign(new AppSettings(), application['settings']);
    for (const key in jsonSchema.definitions.AppSettings['properties']) {
        const syncRule = getReflectMetadata(applicationV2.settings, key);
        if (syncRule && syncRule.manifest) {
            const path = syncRule.manifest.path();
            const exportKey = syncRule.manifest.key ?? key;
            let manifestSection = getManifestSectionByPathV2(exportResults.manifest, path);
            if (application.settings && application.settings[key] !== undefined) {
                manifestSection = manifestSection ?? ensureManifestSectionByPathV2(exportResults.manifest, path);
                const prevValue = manifestSection[exportKey];
                manifestSection[exportKey] = application.settings[key];
                // If the previous value was an object with entries and now it is an empty object, call deleteEmptyStructure
                if (
                    typeof prevValue === 'object' &&
                    Object.keys(prevValue).length > 0 &&
                    typeof manifestSection[exportKey] === 'object' &&
                    Object.keys(manifestSection[exportKey]).length === 0
                ) {
                    delete manifestSection[exportKey];
                    deleteEmptyStructure(exportResults.manifest, path, false);
                }
            } else if (manifestSection && exportKey in manifestSection) {
                delete manifestSection[exportKey];
                deleteEmptyStructure(exportResults.manifest, path, false);
            }
        }
    }
    // transfer pages and routings
    const manifestPages = exportResults.manifest[ManifestSection.generic]?.pages;
    if (manifestPages && application['pages']) {
        transformPageToNewManifestV2(application.home, application['pages'], Object.values(manifestPages)[0] as object);
    }
    return exportResults;
};

/**
 * Exports a Fiori Element V2 page.
 * It converts the entries of the input page (config file) to corresponding manifest settings and flex changes.
 *
 * @param exportParametersV2 - list of parameters for the export; the list varies for the different schema types
 * @param ui5Version - The version of SAP UI5 required for the export
 * @returns ExportResults - The export result comprises the enhanced manifest as well as a list of flex changes.
 */
export function exportPageV2(
    exportParametersV2: ExportParametersV2[FioriElementsVersion.v2],
    ui5Version: UI5Version
): ExportResults | undefined {
    if (exportParametersV2[SchemaType.ListReport]) {
        return exportListReportPageV2(exportParametersV2[SchemaType.ListReport], ui5Version);
    } else if (exportParametersV2[SchemaType.ObjectPage]) {
        return exportObjectPage(exportParametersV2[SchemaType.ObjectPage], ui5Version);
    } else if (exportParametersV2[SchemaType.OverviewPage]) {
        return exportOverviewPage(exportParametersV2[SchemaType.OverviewPage]);
    } else if (exportParametersV2[SchemaType.Application]) {
        const { application, manifest, jsonSchema } = exportParametersV2[SchemaType.Application];
        return exportApplicationV2(application, manifest, jsonSchema);
    } else if (exportParametersV2[SchemaType.AnalyticalListPage]) {
        return exportAnalyticalListPage(exportParametersV2[SchemaType.AnalyticalListPage], ui5Version);
    }
}

/**
 * Exports the config change of a single entity of the config, as indicated by an entityPath.
 * It converts the entries of the input page (config file) to corresponding manifest settings and flex changes.
 *
 * @param exportParametersV2 - list of parameters for the export; the list varies for the different schema types
 * @param ui5Version - the minUi5Version of the app
 * @param deletionRequest - if set to true, any manifest setting specified by entityPath gets deleted even if it comprises any unknown property
 * @returns ExportResults - The export result comprises the enhanced manifest as well as a list of flex changes.
 */
export function exportConfigEntityByPathV2(
    exportParametersV2: ExportV2WithPath[FioriElementsVersion.v2],
    ui5Version: UI5Version,
    deletionRequest = false
): ExportResults | undefined {
    if (exportParametersV2[SchemaType.ListReport]) {
        return exportListReportPageV2(exportParametersV2[SchemaType.ListReport], ui5Version, deletionRequest);
    } else if (exportParametersV2[SchemaType.ObjectPage]) {
        return exportObjectPage(exportParametersV2[SchemaType.ObjectPage], ui5Version, deletionRequest);
    } else if (exportParametersV2[SchemaType.OverviewPage]) {
        return exportOverviewPage(exportParametersV2[SchemaType.OverviewPage]);
    } else if (exportParametersV2[SchemaType.AnalyticalListPage]) {
        return exportAnalyticalListPage(exportParametersV2[SchemaType.AnalyticalListPage], ui5Version, deletionRequest);
    }
}
